import type { LatLng } from "leaflet";
import _ from "lodash";
import { type RefObject, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

import { markerRadius } from "@/constants/marker-constants";
import Light from "@/models/light";
import MapSettings from "@/models/map-settings";
import type { MarkerPosition } from "@/models/marker-position";
import type { MapMode } from "@/types";
import { ConfigurationManager } from "@/utils";
import { getLightArrayPositions, snapToGrid } from "@/utils/map";

/**
 * Props for the useFixturePlacement hook.
 */
interface FixturePlacementProps {
    /**
     * Callback to add new lights to the map.
     */
    addLights: (lights: Light[]) => void;
    /**
     * Callback to add a temporary marker to mode state.
     */
    addTemporaryMarker: (marker: MarkerPosition) => void;
    /**
     * The light array configuration used when placing more than one fixture.
     */
    arrayOfLights: {
        lights: number;
        direction: string;
        spacing: number;
    };
    /**
     * A ref holding the current lights, used for overlap checks.
     */
    lightsRef: RefObject<Light[]>;
    /**
     * The map settings configuration.
     */
    mapSettings: MapSettings;
    /**
     * A ref holding the current markers, used for overlap checks.
     */
    markersRef: RefObject<Record<string, MarkerPosition>>;
    /**
     * The active map mode.
     */
    mode: MapMode;
    /**
     * Callback to select a marker.
     */
    selectMarker: (marker: MarkerPosition) => void;
    /**
     * Whether new fixtures snap to the ceiling tile grid.
     */
    snapping: boolean;
    /**
     * The toast notification callback.
     */
    toastr: (message: string, type?: string) => void;
}

const overlapMessage =
    "We couldn't place all of the lights in your array. " +
    "The skipped lights were either out of bounds of the map dimensions, or overlapped existing lights.";

/**
 * Places new lights and markers on the map, honouring grid snapping, the light
 * array configuration, and overlap rejection.
 *
 * @param params - The placement configuration, fixture refs, and store callbacks.
 * @returns The click handler that places whichever fixture the mode calls for.
 */
export const useFixturePlacement = ({
    addLights,
    addTemporaryMarker,
    arrayOfLights,
    lightsRef,
    mapSettings,
    markersRef,
    mode,
    selectMarker,
    snapping,
    toastr,
}: FixturePlacementProps) => {
    const handleAddMarker = useCallback(
        (centerPoint: LatLng, floorSpaceId: number) => {
            const placementPoint = snapping
                ? snapToGrid(centerPoint, mapSettings.ceilingDetails)
                : centerPoint;

            const hasOverlap = Object.values(markersRef.current).some(
                (m) =>
                    Math.abs(m.x - placementPoint.lng) < markerRadius * 2 &&
                    Math.abs(m.y - placementPoint.lat) < markerRadius * 2,
            );

            if (hasOverlap) return;

            const newMarker = {
                uniqueId: uuidv4(),
                id: 0,
                floorSpaceId,
                markerType: mode.props.markerType,
                identifier: null,
                notes: null,
                x: placementPoint.lng,
                y: placementPoint.lat,
                z: 0,
            } as MarkerPosition;

            addTemporaryMarker(newMarker);
            selectMarker(newMarker);
        },
        [
            addTemporaryMarker,
            mapSettings.ceilingDetails,
            markersRef,
            mode.props.markerType,
            selectMarker,
            snapping,
        ],
    );

    const handleAddLight = useCallback(
        (centerPoint: LatLng, floorSpaceId: number) => {
            const {
                exteriorWidth,
                exteriorLength,
                reportingType,
                displayName,
                luminaireTypeID,
                interiorLength,
                interiorWidth,
            } = mode.props.lightType;

            const type = {
                isCLS: reportingType === "cls",
                name: displayName,
                id: luminaireTypeID,
                width: parseFloat(exteriorWidth),
                length: parseFloat(exteriorLength),
                interiorWidth,
                interiorLength,
            };

            const newLights: Light[] = [];
            let anyLightOverlapped = false;

            const createLight = (point: LatLng) => {
                const newLight = new Light(
                    point,
                    type,
                    floorSpaceId,
                    undefined,
                );

                const isOverlapping = newLight.isOverlapping([
                    ...lightsRef.current,
                    ...newLights,
                ]);

                if (!isOverlapping) {
                    newLights.push(newLight);
                } else if (anyLightOverlapped === false) {
                    anyLightOverlapped = true;
                }
            };

            let snappedCenterPoint = centerPoint;

            if (snapping) {
                const anchorLight = new Light(
                    centerPoint,
                    type,
                    floorSpaceId,
                    undefined,
                );

                anchorLight.snappingToGrid(mapSettings);
                snappedCenterPoint = anchorLight.point;
            }

            const lightsPoints = getLightArrayPositions(
                snappedCenterPoint,
                arrayOfLights.lights,
                arrayOfLights.direction,
                arrayOfLights.spacing,
                type,
            );

            _.forEach(lightsPoints, createLight);

            if (anyLightOverlapped) {
                toastr(overlapMessage, "warning");
            }

            if (newLights.length > 0) {
                addLights(newLights);
            }
        },
        [
            addLights,
            arrayOfLights.direction,
            arrayOfLights.lights,
            arrayOfLights.spacing,
            lightsRef,
            mapSettings,
            mode.props.lightType,
            toastr,
            snapping,
        ],
    );

    const onAddFixtures = useCallback(
        (centerPoint: LatLng) => {
            const { floorSpaceId } = ConfigurationManager.values;

            if (mode.props?.markerType) {
                handleAddMarker(centerPoint, floorSpaceId);
                return;
            }

            handleAddLight(centerPoint, floorSpaceId);
        },
        [handleAddLight, handleAddMarker, mode.props.markerType],
    );

    return { onAddFixtures };
};
